import type { RootState } from '..';
import { createSelector, createStructuredSelector } from 'reselect';

type StateWithOptionalAuth = Omit<RootState, 'auth'> & Partial<Pick<RootState, 'auth'>>;

const getAuth = (state: StateWithOptionalAuth): RootState['auth'] | undefined => state.auth;

/**
 * @param state the RootState
 * @returns Will always be the root user id, even if the user is masquerading.
 */
export const getRootUser = (state: StateWithOptionalAuth): RootState['auth']['user'] => {
    return getAuth(state)?.user;
};

/**
 * @param state the RootState
 * @returns Will be the masqueraded user id, if the user is masquerading.
 */
export const getUser = (state: StateWithOptionalAuth): RootState['auth']['user'] =>
    getAuth(state)?.masqUser ?? getAuth(state)?.user;

const getAuthAccessToken = (state: StateWithOptionalAuth) => getAuth(state)?.accessToken;
const getAuthMasqAccessToken = (state: StateWithOptionalAuth) => getAuth(state)?.masqAccessToken;

export const getAccessTokens = createStructuredSelector({
    accessToken: getAuthAccessToken,
    masqAccessToken: getAuthMasqAccessToken,
});

export const getClientCredentials = (state: StateWithOptionalAuth): RootState['auth']['clientCredentials'] =>
    getAuth(state)?.clientCredentials;

export const getCurrentAccessToken = createSelector(getAccessTokens, (tokens) => {
    return tokens?.masqAccessToken?.token ?? tokens?.accessToken?.token;
});

export const getIsSuperuserEnabledFlag = (state: RootState): RootState['ui']['superuserAccessEnabledUntil'] =>
    state.ui.superuserAccessEnabledUntil;
export const getCurrentIsSuperuserEnabledFlag = createSelector(getIsSuperuserEnabledFlag, (flag) => Boolean(flag));
